'use strict';

// Shared by the streaming server and the browser. Model output is data, never code.
const own = (o, k) => Object.prototype.hasOwnProperty.call(o || {}, k);
const word = value => typeof value === 'string' && /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/.test(value) && !['constructor', 'prototype', '__proto__'].includes(value);
const str = (v, n = 800) => typeof v === 'string' ? v.slice(0, n) : '';
const num = (v, fallback = 0, min = -1e12, max = 1e12) => typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;
const list = (v, n) => Array.isArray(v) ? v.slice(0, n) : [];
function safeURL(value, image = false) {
  try {
    const u = new URL(value);
    if (!(image ? ['https:'] : ['https:', 'http:']).includes(u.protocol) || u.username || u.password) return null;
    if (image && (/^(localhost|127\.|10\.|192\.168\.|169\.254\.|0\.|\[|172\.(1[6-9]|2\d|3[01])\.)/i.test(u.hostname) || !u.hostname.includes('.'))) return null;
    return u.href;
  } catch { return null; }
}

// Small arithmetic language: no eval, member access, strings, assignments or loops.
function calculate(expression, values = {}) {
  if (typeof expression !== 'string' || expression.length > 400) return null;
  const tokens = expression.match(/(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?|[A-Za-z][A-Za-z0-9_]*|[+*/^(),-]|\S/g) || [];
  let i = 0, steps = 0;
  const tick = () => { if (++steps > 200) throw Error('budget'); };
  const funcs = { min: (...x) => Math.min(...x), max: (...x) => Math.max(...x), abs: Math.abs, sqrt: Math.sqrt, round: Math.round, floor: Math.floor, ceil: Math.ceil, pow: Math.pow };
  function primary() {
    tick(); const token = tokens[i++];
    if (token === '(') { const v = sum(); if (tokens[i++] !== ')') throw Error('parenthesis'); return v; }
    if (/^(?:\d|\.)/.test(token || '')) return Number(token);
    if (!word(token)) throw Error('token');
    if (tokens[i] === '(') {
      if (!own(funcs, token)) throw Error('function');
      i++; const args = [sum()]; while (tokens[i] === ',') { i++; args.push(sum()); if (args.length > 8) throw Error('arguments'); }
      if (tokens[i++] !== ')') throw Error('arguments');
      return funcs[token](...args);
    }
    if (!own(values, token) || !['number', 'boolean'].includes(typeof values[token])) throw Error('variable');
    return Number(values[token]);
  }
  function power() { const a = primary(); if (tokens[i] === '^') { i++; return Math.pow(a, unary()); } return a; }
  function unary() { tick(); if (tokens[i] === '+' || tokens[i] === '-') { const op = tokens[i++]; return (op === '-' ? -1 : 1) * unary(); } return power(); }
  function product() { let v = unary(); while (tokens[i] === '*' || tokens[i] === '/') { const op = tokens[i++], b = unary(); v = op === '*' ? v * b : v / b; tick(); } return v; }
  function sum() { let v = product(); while (tokens[i] === '+' || tokens[i] === '-') { const op = tokens[i++], b = product(); v = op === '+' ? v + b : v - b; tick(); } return v; }
  try { const result = sum(); return i === tokens.length && Number.isFinite(result) && Math.abs(result) <= 1e15 ? result : null; } catch { return null; }
}
function interpolate(template, values) {
  return str(template, 2000).replace(/\{\{([a-zA-Z][a-zA-Z0-9_]{0,39})\}\}/g, (_, key) => own(values, key) ? String(values[key]).slice(0, 500) : '—');
}
function field(raw, index) {
  const type = ['input', 'textarea', 'select', 'radio', 'checkbox', 'date', 'number', 'slider', 'switch'].includes(raw?.type) ? raw.type : 'input';
  const options = list(raw?.options, 12).map(x => str(x, 100)).filter(Boolean);
  const min = num(raw?.min, 0), max = num(raw?.max, 100);
  return { id: word(raw?.id) ? raw.id : 'field' + index, type, label: str(raw?.label, 160) || '输入', placeholder: str(raw?.placeholder, 100), options,
    min, max: Math.max(min, max), step: num(raw?.step, 1, .0001, 1e10), required: raw?.required === true,
    value: ['checkbox', 'switch'].includes(type) ? raw?.value === true : ['number', 'slider'].includes(type) ? num(raw?.value, min, min, Math.max(min, max)) : str(raw?.value, 500), unit: str(raw?.unit, 30) };
}
function compileLegacyPanel(raw) {
  if (!raw || raw.version !== 1 || !Array.isArray(raw.components) || !raw.components.length) return { error: '组件格式不完整' };
  const components = [], ids = new Set();
  for (const [index, r] of list(raw.components, 20).entries()) {
    if (!r || typeof r !== 'object') continue;
    let id = word(r.id) && !ids.has(r.id) ? r.id : 'item' + index;
    while (ids.has(id)) id += '_'; ids.add(id);
    const c = { id, type: r.type, title: str(r.title, 160), description: str(r.description, 1200), source: num(r.source, 0, 0, 99), badge: str(r.badge, 40), tooltip: str(r.tooltip, 300) };
    switch (r.type) {
      case 'text': c.text = str(r.text, 3000); break;
      case 'metric': c.value = typeof r.value === 'number' && Number.isFinite(r.value) ? String(r.value) : str(r.value, 120); c.unit = str(r.unit, 30); break;
      case 'image': c.url = safeURL(r.url, true); c.alt = str(r.alt, 300); c.caption = str(r.caption, 500); c.imageQuery = str(r.imageQuery || r.query || r.alt || r.caption, 120); if (!c.imageQuery) continue; break;
      case 'button':
        c.label = str(r.label, 100) || '继续探索'; c.action = (typeof r.action === 'object' ? r.action?.type : r.action) === 'link' ? 'link' : 'prompt'; c.url = safeURL(r.url || r.action?.url); c.prompt = str(r.prompt || r.action?.prompt, 1800);
        if (c.action === 'link' ? !c.url : !c.prompt) continue; break;
      case 'slider': case 'switch': c.field = field({ ...r, type: r.type }, index); break;
      case 'form': case 'calculator': {
        c.fields = list(r.fields, 12).map(field); const unique = new Set();
        c.fields = c.fields.filter(f => !unique.has(f.id) && unique.add(f.id));
        c.outputs = list(r.outputs, 8).map((o, n) => ({ id: word(o?.id) ? o.id : 'result' + n, label: str(o?.label, 100), expression: str(o?.expression, 400), unit: str(o?.unit, 30), decimals: Math.round(num(o?.decimals, 2, 0, 6)) }));
        c.submitLabel = str(r.submitLabel, 80) || '生成预览'; c.template = str(r.template, 2000); c.followup = str(r.followup, 2000); break;
      }
      case 'chart':
        c.kind = r.kind === 'line' ? 'line' : 'bar'; c.unit = str(r.unit, 30); c.editable = r.editable === true;
        c.data = list(r.data, 24).filter(d => typeof d?.value === 'number' && Number.isFinite(d.value) && Math.abs(d.value) <= 1e12).map(d => ({ label: str(d?.label, 100), value: d.value })); if (!c.data.length) continue; break;
      case 'table':
        c.columns = list(r.columns, 10).filter(o => word(o?.key)).map(o => ({ key: o.key, label: str(o.label, 100) }));
        c.columns = c.columns.filter((o, i, a) => a.findIndex(v => v.key === o.key) === i);
        c.rows = list(r.rows, 30).map((row, i) => ({ id: 'row' + i, cells: Object.fromEntries(c.columns.map(o => [o.key, typeof row?.cells?.[o.key] === 'number' && Number.isFinite(row.cells[o.key]) ? row.cells[o.key] : str(row?.cells?.[o.key], 500)])), source: num(row?.source, 0, 0, 99) }));
        c.sortable = r.sortable !== false; if (!c.columns.length || !c.rows.length) continue; break;
      case 'diagram':
        c.nodes = list(r.nodes, 12).map((n, j) => ({ id: word(n?.id) ? n.id : 'node' + j, label: str(n?.label, 80), detail: str(n?.detail, 1500), formula: str(n?.formula, 1500) })).filter((n, j, a) => a.findIndex(v => v.id === n.id) === j);
        c.edges = list(r.edges, 20).map(e => ({ from: str(e?.from, 40), to: str(e?.to, 40), label: str(e?.label, 80) })).filter(e => c.nodes.some(n => n.id === e.from) && c.nodes.some(n => n.id === e.to));
        if (!c.nodes.length) continue; break;
      case 'map':
        c.city = str(r.city, 80); c.connect = r.connect === true;
        c.places = list(r.places, 8).filter(p => (typeof p?.lat === 'number' && typeof p?.lon === 'number' && Math.abs(p.lat) <= 90 && Math.abs(p.lon) <= 180) || str(p?.query, 80)).map(p => ({ name: str(p.name || p.query, 160), lat: typeof p.lat === 'number' && Number.isFinite(p.lat) ? p.lat : null, lon: typeof p.lon === 'number' && Number.isFinite(p.lon) ? p.lon : null, query: str(p.query, 80), city: str(p.city || r.city, 80), description: str(p.description, 500) }));
        if (!c.places.length && !c.city) continue; break;
      case 'weather': c.city = str(r.city, 80); c.date = /^\d{4}-\d{2}-\d{2}$/.test(r.date || '') ? r.date : ''; if (!c.city) continue; break;
      case 'cards': case 'carousel':
        c.variant = ['poster', 'editorial', 'illustrated', 'compact', 'default'].includes(r.variant) ? r.variant : 'default';
        c.items = list(r.items, 12).map(item => ({ title: str(item?.title, 160), text: str(item?.text, 1400), badge: str(item?.badge, 50), avatar: str(item?.avatar, 4), url: safeURL(item?.url), image: safeURL(item?.image, true), imageQuery: str(item?.imageQuery, 120), posterQuery: str(item?.posterQuery, 120), year: Math.round(num(item?.year, 0, 0, 2099)), runtime: Math.round(num(item?.runtime, 0, 0, 1000)), tags: list(item?.tags, 6).map(t => str(t, 30)).filter(Boolean), prompt: str(item?.prompt, 1200), metadata: list(item?.metadata, 5).map(m => ({ label: str(m?.label, 40), value: str(m?.value, 180) })), source: num(item?.source, 0, 0, 99) }));
        if (!c.items.length) continue; break;
      case 'timeline': case 'checklist':
        c.items = list(r.items, 20).filter(item => str(item?.title, 160)).map(item => ({ title: str(item.title, 160), text: str(item.text, 1200), time: str(item.time, 80), checked: item.checked === true, source: num(item.source, 0, 0, 99) }));
        if (!c.items.length) continue; break;
      default: continue;
    }
    components.push(c);
  }
  return components.length ? { version: 1, title: str(raw.title, 160), description: str(raw.description, 1000), components } : { error: '暂不支持这些组件，请查看正文说明' };
}

// v2 describes a bounded composition tree rather than a topic-specific template.
const choice = (value, allowed, fallback) => allowed.includes(value) ? value : fallback;
function compilePanel(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { error: '组件格式不完整' };
  // Accept unambiguous schema drift; unknown versions still fail closed.
  if (raw.version == null && Array.isArray(raw.components)) raw = { ...raw, version: 2 };
  else if (raw.version === '1' || raw.version === '2') raw = { ...raw, version: Number(raw.version) };
  if (raw?.version !== 2) return compileLegacyPanel(raw);
  if (!Array.isArray(raw.components) || !raw.components.length) return { error: '组件格式不完整' };
  let count = 0, clipped = false;
  const ids = new Set();
  function node(r, depth) {
    if (!r || typeof r !== 'object' || Array.isArray(r)) return null;
    if (depth > 5 || count >= 64) { clipped = true; return null; }
    const index = count++;
    let id = word(r.id) && !ids.has(r.id) ? r.id : 'node' + index;
    while (ids.has(id)) id += '_'; ids.add(id);
    const base = { id, title: str(r.title,160), description: str(r.description,1200), source: Math.round(num(r.source,0,0,99)),
      tone: choice(r.tone,['neutral','blue','green','amber','rose'],'neutral'),
      surface: choice(r.surface,['plain','soft','outline'],'plain'),
      gap: choice(r.gap,['none','small','medium','large'],'medium'),
      align: choice(r.align,['start','center','end','stretch'],'stretch'),
      size: choice(r.size,['small','body','large','display'],'body'),
      span: Math.round(num(r.span,1,1,4)) };
    if (word(r.when?.field) && ['string','number','boolean'].includes(typeof r.when.equals) && (typeof r.when.equals !== 'number' || Number.isFinite(r.when.equals))) base.when = { field:r.when.field, equals:typeof r.when.equals === 'string' ? str(r.when.equals,100) : r.when.equals };
    let c;
    if (['stack','row','grid','section','tabs','disclosure'].includes(r.type)) {
      const children = list(r.children,20).map(child=>node(child,depth+1)).filter(Boolean);
      if (Array.isArray(r.children) && r.children.length>20) clipped=true;
      if (!children.length) return { ...base,type:'text',text:str(r.fallback,1000)||'这部分内容暂时无法显示，请查看正文说明。' };
      c = { ...base,type:r.type,children,columns:Math.round(num(r.columns,2,1,4)),ratio:choice(r.ratio,['equal','wide-left','wide-right'],'equal'),open:r.open===true };
    } else if (r.type === 'chart') {
      const data=list(r.data,24).filter(d=>str(d?.expression,400)||(typeof d?.value==='number'&&Number.isFinite(d.value)&&Math.abs(d.value)<=1e12)).map(d=>({label:str(d.label,100),value:typeof d.value==='number'?num(d.value):null,expression:str(d.expression,400)}));
      c={...base,type:'chart',kind:r.kind==='line'?'line':'bar',data,unit:str(r.unit,30),dynamic:data.some(d=>d.expression),editable:r.editable===true&&!data.some(d=>d.expression)};
    } else if (r.type === 'field') {
      c = { ...base,type:'field',field:field({...r,id,type:r.control},index) };
    } else if (r.type === 'result') {
      c = { ...base,type:'result',expression:str(r.expression,400),unit:str(r.unit,30),decimals:Math.round(num(r.decimals,2,0,6)) };
    } else if (r.type === 'heading' || r.type === 'badge') {
      c = { ...base,type:r.type,text:str(r.text,300) };
    } else if (r.type === 'divider') c={...base,type:'divider'};
    else if (r.type === 'formula') c={...base,type:'formula',tex:str(r.tex,1500)};
    else if (r.type === 'progress') c={...base,type:'progress',value:num(r.value,0,0,100),expression:str(r.expression,400),label:str(r.label,160)};
    else {
      const legacy = compileLegacyPanel({version:1,components:[r]}).components?.[0];
      c = legacy ? {...legacy,...base} : {...base,type:'text',text:str(r.fallback,1000)||str(r.text,1000)||'这部分内容暂时无法显示，请查看正文说明。'};
      if (c.type === 'text') c.template=str(r.template,2000);
      if (c.type === 'image') { c.aspect=choice(r.aspect,['square','landscape','portrait'],'landscape');c.poster=r.poster===true;c.year=Math.round(num(r.year,0,0,2099)); }
    }
    return c;
  }
  const components=list(raw.components,20).map(r=>node(r,0)).filter(Boolean);
  if(raw.components.length>20)clipped=true;
  return components.length ? {version:2,title:str(raw.title,160),description:str(raw.description,1000),components,truncated:clipped} : {error:'组件格式不完整'};
}
function compositionScope(components, values = {}) {
  const all=[];const visit=nodes=>nodes.forEach(n=>{all.push(n);if(n.children)visit(n.children)});visit(components);
  const scope={};
  for(const n of all) if(n.type==='field') {
    const f=n.field,v=own(values,n.id)?values[n.id]:f.value;
    if(['number','slider'].includes(f.type) ? typeof v==='number'&&Number.isFinite(v)&&v>=f.min&&v<=f.max : ['checkbox','switch'].includes(f.type) ? typeof v==='boolean' : typeof v==='string'&&(!['select','radio'].includes(f.type)||f.options.includes(v))) scope[n.id]=v;
  }
  for(const n of all) if(n.type==='result') {const value=calculate(n.expression,scope);if(value!==null)scope[n.id]=value;}
  return scope;
}

// Repair only punctuation outside strings and literal control characters inside
// strings. Never evaluate code, guess missing values or close truncated JSON.
function parsePanelJSON(text) {
  const input = text.trim();
  try { return JSON.parse(input); } catch { /* Try bounded, lossless repairs. */ }
  let result = '', quoted = false, escaped = false;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (quoted) {
      if (escaped) { result += ch; escaped = false; }
      else if (ch === '\\') { result += ch; escaped = true; }
      else if (ch === '"') { result += ch; quoted = false; }
      else result += ch.charCodeAt(0) < 32 ? JSON.stringify(ch).slice(1, -1) : ch;
    } else if (ch === '"') { result += ch; quoted = true; }
    else if (ch === ',') {
      let next = i + 1; while (/\s/.test(input[next] || '') && next < input.length) next++;
      if (input[next] !== '}' && input[next] !== ']') result += ch;
    } else result += ch;
  }
  return JSON.parse(result);
}

// Only dedicated fenced blocks are compiled; ordinary code/Markdown remains inert.
function splitUI(text, cache, { final = false } = {}) {
  text = typeof text === 'string' ? text : '';
  const parts = []; let cursor = 0, index = 0;
  const fences = /^([ \t]{0,3})(`{3,}|~{3,})([^\n]*)\n/gm; let match;
  while ((match = fences.exec(text))) {
    const marker = match[2][0], length = match[2].length;
    const close = new RegExp('^[ \\t]{0,3}' + marker + '{' + length + ',}[ \\t]*\\r?(?:\\n|$)', 'gm'); close.lastIndex = fences.lastIndex;
    const end = close.exec(text), isUI = match[3].trim().toLowerCase() === 'qomo-ui';
    if (!isUI) { fences.lastIndex = end ? close.lastIndex : text.length; continue; }
    if (match.index > cursor) parts.push({ type: 'text', text: text.slice(cursor, match.index) });
    const stop = end ? close.lastIndex : text.length;
    const json = text.slice(fences.lastIndex, end ? end.index : text.length);
    const raw = text.slice(match.index, stop);
    let hash = 2166136261; for (const ch of raw.trim()) hash = Math.imul(hash ^ ch.charCodeAt(0), 16777619);
    const key = 'ui-' + (hash >>> 0).toString(36);
    let panel;
    if (end || final) {
      const stored = cache?.get(key);
      if (stored?.raw === raw.trim()) panel = stored.panel;
      else {
        try { panel = json.length <= 30000 ? compilePanel(parsePanelJSON(json)) : { error: '组件内容过长' }; } catch { panel = { error: '组件格式暂时无法解析，请查看正文说明' }; }
        if (cache) { if (cache.size >= 12) cache.delete(cache.keys().next().value); cache.set(key, { raw: raw.trim(), panel }); }
      }
    }
    parts.push({ type: end || (final && !panel?.error) ? 'ui' : 'pending-ui', index: index++, key, panel, raw });
    cursor = stop; fences.lastIndex = stop;
  }
  if (cursor < text.length) parts.push({ type: 'text', text: text.slice(cursor) });
  return parts;
}

module.exports = { compilePanel, compositionScope, splitUI, calculate, interpolate, safeURL };
